import type { FC, KeyboardEventHandler, SetStateAction } from 'react'
import { useRef, useState } from 'react'

import type { ValueLabelPair } from '@skytech/ui'
import {
  Input,
  Select,
  MultiSelect,
  createOption,
  MessagePopup,
  StyledText,
  Checkbox,
} from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import {
  COUNTRY_FLAGS,
  countryOptions,
  getDefaultCountryId,
  getEntries,
  companyNamesByWhiteLabelId,
  getLocaleTimezoneIsoDate,
  getIsDateInRange,
  getWhiteLabelForDealerStyle,
} from '@skytech/utils'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { FieldProps, FormikProps } from 'formik'
import { Formik, Form, Field } from 'formik'

import { demoCustomersResources } from '@/api/demoCustomersApi'
import queries from '@/api/queries'
import { useAppContext } from '@/App.context'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import { useProfile } from '@/hooks/user'
import { DAY } from '@/util/formatters/dates'
import { getErrorCode } from '@/util/request'

type Values = {
  customerName: string
  customerCount: number
  customerNames: string[]
  countryId: number
  whitelabelId: number
  numberOfDepartmentsPerCustomer: number
  numberOfCostObjectsPerDepartment: number
  isExternalCustomer: boolean
  startDate: string
  expiryDate: string
}

const TWO_DAYS = DAY * 2
const FOURTEEN_DAYS = DAY * 14

export const DemoCustomerForm: FC<{
  onClose: () => void
}> = ({ onClose }) => {
  const { locale, whiteLabel } = useAppContext()
  const translate = useTranslation()
  const queryClient = useQueryClient()
  const userWhiteLabelId = useProfile()?.whitelabelId
  if (!userWhiteLabelId)
    throw new Error("Something went wrong while fetching the user's white label!")

  const formRef = useRef<FormikProps<Values> | null>(null)
  const [creatableValue, setCreatableValue] = useState<ValueLabelPair[]>([])
  const [inputValue, setInputValue] = useState('')
  const [errorResponse, setErrorResponse] = useState<string | null>(null)
  const defaultPhoneCountryId = getDefaultCountryId(locale)
  const requiredMessage = translate('This field is mandatory')
  const lessThanMessage = translate('Please select a value that is less than 10')

  const setDate = (startDate: number, daysAhead: number) => new Date(startDate + daysAhead)
  const validDate = (date: string, daysAhead: number) => {
    if (!date) return ''

    const time = new Date(date).getTime()
    if (isNaN(time)) return ''

    return setDate(time, daysAhead)
  }
  const dateNow = Date.now()
  const today = getLocaleTimezoneIsoDate(new Date(dateNow))
  const twoDaysAhead = getLocaleTimezoneIsoDate(setDate(dateNow, TWO_DAYS))
  const fourteenDaysAhead = getLocaleTimezoneIsoDate(setDate(dateNow, FOURTEEN_DAYS))

  const initialValues = {
    customerName: '',
    customerCount: 0,
    customerNames: [],
    countryId: defaultPhoneCountryId,
    whitelabelId: userWhiteLabelId,
    numberOfDepartmentsPerCustomer: 1,
    numberOfCostObjectsPerDepartment: 10,
    isExternalCustomer: false,
    startDate: today,
    expiryDate: twoDaysAhead,
  }

  const handleKeyDown: KeyboardEventHandler = event => {
    if (!inputValue) return
    switch (event.key) {
      case 'Enter':
      case 'Tab':
        setCreatableValue(prev => [...prev, createOption(inputValue)])
        setInputValue('')
        event.preventDefault()
    }
  }

  const validate = (values: Values) => {
    const errors: Partial<Record<keyof Values, string>> = {}
    if (!creatableValue?.length) {
      errors.customerNames = requiredMessage
    }
    if (!values.countryId) {
      errors.countryId = requiredMessage
    }
    if (!values.numberOfCostObjectsPerDepartment) {
      errors.numberOfCostObjectsPerDepartment = requiredMessage
    } else if (values.numberOfCostObjectsPerDepartment > 10) {
      errors.numberOfCostObjectsPerDepartment = lessThanMessage
    }
    if (!values.numberOfDepartmentsPerCustomer) {
      errors.numberOfDepartmentsPerCustomer = requiredMessage
    } else if (values.numberOfDepartmentsPerCustomer > 10) {
      errors.numberOfDepartmentsPerCustomer = lessThanMessage
    }
    if (!values.whitelabelId) {
      errors.whitelabelId = requiredMessage
    }
    if (values.isExternalCustomer && !values.startDate) {
      errors.startDate = requiredMessage
    }
    if (values.isExternalCustomer && !values.expiryDate) {
      errors.expiryDate = requiredMessage
    }

    if (
      values.isExternalCustomer &&
      values.expiryDate &&
      values.startDate !== '' &&
      !getIsDateInRange({
        minDate: new Date(validDate(values.startDate, TWO_DAYS)),
        maxDate: new Date(validDate(values.startDate, FOURTEEN_DAYS)),
        options: {
          date: new Date(values.expiryDate),
        },
      })
    ) {
      errors.expiryDate = translate('Invalid date range')
    }

    return errors
  }

  const demoCustomerMutation = useMutation(demoCustomersResources.postDemoCustomer, {
    onSuccess() {
      queryClient.invalidateQueries(queries.demoCustomers())
      onClose()
    },
    onError(error: { text: string; response: Response; json: Object }) {
      const errorText =
        getErrorCode(error) === 409 ? `Customer already exists` : 'Could not save customer'
      setErrorResponse(errorText)
    },
  })

  const handleSubmit = (formData: Values) => {
    if (!formRef.current?.dirty) return

    const demoCustomerPayload = {
      customerNames: creatableValue.map((item: ValueLabelPair) => item.label) as string[],
      customerName: creatableValue[0].label as string,
      customerCount: creatableValue.length,
      countryId: formData.countryId,
      whitelabelId: formData.whitelabelId,
      numberOfDepartmentsPerCustomer: formData.numberOfDepartmentsPerCustomer,
      numberOfCostObjectsPerDepartment: formData.numberOfCostObjectsPerDepartment,
      startDate: formData.isExternalCustomer ? formData.startDate : null,
      expiryDate: formData.isExternalCustomer ? formData.expiryDate : null,
    }
    demoCustomerMutation.mutate(demoCustomerPayload)
  }

  return (
    <>
      <h1 className="center">
        <Translation id="New demo customer" />
      </h1>
      <Formik
        innerRef={formRef}
        initialValues={initialValues}
        validate={validate}
        onSubmit={handleSubmit}
      >
        {({ isValid, values }) => (
          <Form>
            <div className="mx-auto flex w-full max-w-sm flex-col">
              <Field name="customerNames">
                {({ meta, field }: FieldProps) => (
                  <MultiSelect
                    isClearable
                    isCreatable
                    capitalizeLabel
                    id="customerNames"
                    label={
                      <>
                        <Translation id="Customer names" />*
                      </>
                    }
                    placeholder={translate('Type customer name and press enter...')}
                    marginBottom={16}
                    options={creatableValue}
                    inputValue={inputValue}
                    handleKeyDown={handleKeyDown}
                    onInputChange={(newValue: React.SetStateAction<string>) =>
                      setInputValue(newValue)
                    }
                    error={meta.error}
                    touched={meta.touched}
                    {...field}
                    onChange={(args: SetStateAction<ValueLabelPair[]>) => {
                      setCreatableValue(args)
                    }}
                  />
                )}
              </Field>

              <Field name="isExternalCustomer">
                {({ field }: FieldProps) => (
                  <Checkbox
                    id="isExternalCustomer"
                    marginBottom={16}
                    label={<Translation id="Used by external customer" />}
                    checked={field.value}
                    whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
                    {...field}
                  />
                )}
              </Field>

              {values.isExternalCustomer && (
                <div className="flex flex-row gap-1">
                  <Field name="startDate">
                    {({ field, meta }: FieldProps) => (
                      <Input
                        capitalizeLabel
                        isFullWidth
                        id="startDate"
                        type="date"
                        wrapperClassName="flex-1"
                        ariaLabel={translate('Start date:')}
                        label={<Translation id="Start date:" />}
                        min={today}
                        max={fourteenDaysAhead}
                        marginBottom={16}
                        touched={meta.touched}
                        error={meta.error}
                        whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
                        {...field}
                      />
                    )}
                  </Field>
                  <Field name="expiryDate">
                    {({ field, meta, form }: FieldProps) => (
                      <Input
                        capitalizeLabel
                        isFullWidth
                        id="expiryDate"
                        type="date"
                        wrapperClassName="flex-1"
                        ariaLabel={translate('Expiry date:')}
                        label={<Translation id="Expiry date:" />}
                        min={getLocaleTimezoneIsoDate(validDate(form.values.startDate, TWO_DAYS))}
                        max={getLocaleTimezoneIsoDate(
                          validDate(form.values.startDate, FOURTEEN_DAYS)
                        )}
                        marginBottom={16}
                        touched={meta.touched}
                        error={meta.error}
                        whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
                        {...field}
                      />
                    )}
                  </Field>
                </div>
              )}

              <Field name="countryId">
                {({ field, meta, form }: FieldProps) => (
                  <Select
                    capitalizeLabel
                    id="countryId"
                    label={
                      <>
                        <Translation id="Country" />*
                      </>
                    }
                    options={countryOptions.map(option => ({
                      value: option.value,
                      label: `${COUNTRY_FLAGS[option.label]} ${option.label}`,
                    }))}
                    marginBottom={16}
                    touched={meta.touched}
                    error={meta.error}
                    {...field}
                    onChange={option => {
                      form.setFieldValue(field.name, option?.value)
                    }}
                  />
                )}
              </Field>

              <Field name="numberOfDepartmentsPerCustomer">
                {({ field, meta }: FieldProps) => (
                  <Input
                    isFullWidth
                    id="numberOfDepartmentsPerCustomer"
                    type="number"
                    capitalizeLabel
                    label={
                      <>
                        <Translation id="Number of departments per customer" />*
                      </>
                    }
                    min="1"
                    max="10"
                    marginBottom={16}
                    error={meta.error}
                    touched={meta.touched}
                    whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
                    {...field}
                  />
                )}
              </Field>

              <Field name="numberOfCostObjectsPerDepartment">
                {({ field, meta }: FieldProps) => (
                  <Input
                    isFullWidth
                    type="number"
                    id="numberOfCostObjectsPerDepartment"
                    capitalizeLabel
                    label={
                      <>
                        <Translation id="Number of employee per department" />*
                      </>
                    }
                    min="1"
                    max="10"
                    marginBottom={16}
                    error={meta.error}
                    touched={meta.touched}
                    whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
                    {...field}
                  />
                )}
              </Field>

              {userWhiteLabelId === 1 && (
                <Field name="whitelabelId">
                  {({ field, meta, form }: FieldProps) => (
                    <Select
                      capitalizeLabel
                      id="whitelabelId"
                      label={<Translation id="White label" />}
                      options={getEntries(companyNamesByWhiteLabelId).map(([value, label]) => ({
                        value: Number(value),
                        label,
                      }))}
                      marginBottom={16}
                      touched={meta.touched}
                      error={meta.error}
                      {...field}
                      onChange={option => {
                        form.setFieldValue(field.name, option?.value)
                      }}
                    />
                  )}
                </Field>
              )}
            </div>
            <div className="flex justify-end">
              <Button
                variant="tertiary"
                onClick={onClose}
                whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
              >
                <Translation id="Cancel" />
              </Button>
              <Button
                disabled={!isValid || demoCustomerMutation.isLoading}
                variant="primary"
                type="submit"
                whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
              >
                {demoCustomerMutation.isLoading ? (
                  <Translation id="Creating..." />
                ) : (
                  <Translation id="Create" />
                )}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
      <MessagePopup
        title={<Translation id="Error" />}
        type="error"
        message={
          <StyledText colorMode="gray" whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}>
            {errorResponse}{' '}
          </StyledText>
        }
        isOpen={Boolean(errorResponse)}
        onClose={() => setErrorResponse('')}
        whiteLabel={getWhiteLabelForDealerStyle(whiteLabel)}
      />
    </>
  )
}
